<script setup lang="ts">
import type { HomepageBanner } from "~/stores/settings";

// When no banners are configured in the hub, fall back to the static premium
// banner so the strip is never empty (matches app/'s search page). Links to the
// premium landing page, same as app/.
const FALLBACK_PREMIUM_URL = "https://landing.anddine.co.uk/premium";

const settingsStore = useSettingsStore();
const { banners, loaded } = storeToRefs(settingsStore);
const { staticUrl } = useImageUrl();
const { track } = useTracking();

// Safety net only. `/search` — the one page that renders this — loads banners as
// part of its own page data so they are in the server-rendered HTML; this covers
// any other caller. `fetchBanners` shares its in-flight promise, so the two
// callers can't double-fetch.
onMounted(() => {
  if (!settingsStore.loaded) settingsStore.fetchBanners();
});

const displayBanners = computed<HomepageBanner[]>(() => {
  if (banners.value.length) return banners.value;
  return [
    {
      id: "premium-fallback",
      name: "premium",
      url: FALLBACK_PREMIUM_URL,
      imageUrl: staticUrl("/Images/search-page/premium-banner.jpg"),
      mobileImageUrl: staticUrl("/Images/search-page/premium-banner-mobile.jpg"),
    },
  ];
});

function onBannerClick(banner: HomepageBanner) {
  track("banner_clicked", { banner_name: banner.name, banner_url: banner.url });
  if (!banner.url) return;
  if (/^https?:\/\//i.test(banner.url)) {
    window.open(banner.url, "_blank");
  } else {
    navigateTo(banner.url);
  }
}
</script>

<template>
  <section
    v-if="banners.length || loaded"
    class="homepage-banners"
  >
    <!-- Everything stays inside a single `ClientOnly`, which is what the carousel
         needs (it measures the container and reads `window.innerWidth`).
         Deliberately NO `#fallback`: a fallback renders on the server and then
         never swaps to the default slot on a hydrated page, so a refresh got a
         static first banner and the slider only ever appeared after a client-side
         navigation. The layout jump the fallback was there to prevent is handled
         by reserving the strip's height in CSS below instead. -->
    <ClientOnly>
      <CarouselBase
        v-if="displayBanners.length > 1"
        :autoplay="true"
        :autoplay-interval="6000"
        nav-position="outside"
      >
        <CarouselSlide
          v-for="banner in displayBanners"
          :key="banner.id"
        >
          <HomepageBannerButton
            :banner="banner"
            @select="onBannerClick"
          />
        </CarouselSlide>
      </CarouselBase>

      <HomepageBannerButton
        v-else
        :banner="displayBanners[0]!"
        @select="onBannerClick"
      />
    </ClientOnly>
  </section>
</template>

<style lang="scss" scoped>
/* `__banner` / `__frame` live in HomepageBannerButton.vue alongside the markup
   they style — scoped styles here would not reach a child component's DOM. */
.homepage-banners {
  width: 100%;
  margin: 1rem 0 0;

  /* Hold the strip's height from the first paint, while the client-only carousel
     inside is still empty. Without this the section is 0rem tall until the
     carousel mounts and the whole page below it jumps down — which is the actual
     problem, not the banner images' weight.

     Same ratios as HomepageBannerButton's `__frame`, so the reserved box matches
     what lands in it. `aspect-ratio` is a preferred size, not a cap: the mounted
     carousel adds its nav row and the section grows the last ~1rem to fit rather
     than clipping. */
  aspect-ratio: 36 / 5;

  @media (max-width: 768px) {
    aspect-ratio: 2 / 1;
  }
}
</style>
